import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que recopilamos',
    text: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; esto es manejado por nuestro procesador de pagos seguro.'
  }, {
    heading: 'Uso de la información',
    text: 'Tu información se utiliza exclusivamente para: entregar el producto digital adquirido, enviar confirmación de compra, y comunicarnos contigo si hay algún problema con tu pedido.'
  }, {
    heading: 'Compartir información',
    text: 'No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios que nos ayudan a operar (procesador de pagos, servicio de email), quienes están obligados a mantener tu información confidencial.'
  }, {
    heading: 'Cookies',
    text: 'Utilizamos cookies básicas para el funcionamiento del sitio. No utilizamos cookies de rastreo publicitario ni compartimos datos de navegación con terceros.'
  }, {
    heading: 'Tus derechos',
    text: 'Puedes solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento enviando un correo a nuestro equipo.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto digital',
    text: 'Reset 4 Pilares (30 días) es un producto digital descargable en formato PDF. Al completar tu compra, recibirás acceso inmediato al material a través de un enlace de descarga enviado a tu correo electrónico.'
  }, {
    heading: 'Uso personal',
    text: 'El contenido adquirido es para uso personal únicamente. No está permitido redistribuir, revender, compartir públicamente o reproducir el material sin autorización expresa.'
  }, {
    heading: 'Política de reembolso',
    text: 'Debido a la naturaleza digital del producto, todas las ventas son finales. Sin embargo, si experimentas problemas técnicos con la descarga, nuestro equipo te asistirá para resolver cualquier inconveniente.'
  }, {
    heading: 'Descargo de responsabilidad',
    text: 'Reset 4 Pilares es una herramienta de organización y bienestar personal. No sustituye asesoría médica, psicológica o profesional. Los resultados pueden variar según el compromiso y circunstancias individuales de cada persona.'
  }, {
    heading: 'Modificaciones',
    text: 'Nos reservamos el derecho de actualizar estos términos en cualquier momento. Los cambios serán efectivos inmediatamente después de su publicación en esta página.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-brown text-cream/60 py-12 text-center text-sm" data-id="element-1">
        <div className="max-w-2xl mx-auto px-6 space-y-4" data-id="element-2">
          <p data-id="element-3">
            © {new Date().getFullYear()} Reset 4 Pilares. Todos los derechos
            reservados.
          </p>
          <p data-id="element-4">Hecho para humanos, no para robots.</p>
          <div className="flex items-center justify-center gap-4 pt-2" data-id="element-5">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/40 hover:text-cream transition-colors underline underline-offset-2" data-id="element-6">
              Privacidad
            </button>
            <span className="text-cream/20" data-id="element-7">•</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream/40 hover:text-cream transition-colors underline underline-offset-2" data-id="element-8">
              Términos
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-9">
        {activeModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-brown/60 backdrop-blur-sm z-[100]" data-id="element-10" />

            {/* Modal Panel */}
            <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-xl md:max-h-[80vh] bg-cream rounded-2xl shadow-2xl z-[101] flex flex-col overflow-hidden" data-id="element-11">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-brown/10 shrink-0" data-id="element-12">
                <h2 className="text-xl font-serif font-bold text-brown" data-id="element-13">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-lg text-brown-light hover:text-brown hover:bg-brown/5 transition-colors" aria-label="Cerrar" data-id="element-14">
                  <X size={20} data-id="element-15" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-6 space-y-6 flex-1" data-id="element-16">
                {content.sections.map((section, i) => <div key={i} data-id="element-17">
                    <h3 className="font-semibold text-brown mb-2 text-base" data-id="element-18">
                      {section.heading}
                    </h3>
                    <p className="text-brown-light text-sm leading-relaxed" data-id="element-19">
                      {section.text}
                    </p>
                  </div>)}
                <p className="text-brown-light/60 text-xs pt-4 border-t border-brown/5" data-id="element-20">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long'
              })}
                </p>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}